import './bootstrap';
import '../css/app.css';

import { createRoot } from 'react-dom/client';
import { createInertiaApp } from '@inertiajs/react';
import { resolvePageComponent } from 'laravel-vite-plugin/inertia-helpers';
import { NotificationProvider } from '@/Utils/NotificationContext';
import '@/Utils/suppressWarnings';

const appName = import.meta.env.VITE_APP_NAME || 'UHL Vault';
const pages = import.meta.glob('./Pages/**/*.jsx');
const inertiaRootId = 'app';
const CHUNK_RELOAD_KEY = 'uhl:chunk-reload:last-url';

const getInitialInertiaPage = (id) => {
    if (typeof document === 'undefined') {
        return undefined;
    }

    const root = document.getElementById(id);
    const rootPage = root?.dataset?.page;
    const scriptPage = Array.from(document.querySelectorAll('script[data-page]'))
        .find((script) => script.dataset.page === id)
        ?.textContent;
    const rawPage = rootPage || scriptPage;

    if (!rawPage || rawPage === 'undefined') {
        throw new Error(`Missing initial Inertia page JSON for #${id}.`);
    }

    return JSON.parse(rawPage);
};

const isDynamicImportFailure = (error) => {
    const message = String(error?.message || error || '');

    return /Failed to fetch dynamically imported module/i.test(message)
        || /Importing a module script failed/i.test(message)
        || /error loading dynamically imported module/i.test(message);
};

const reloadForChunkFailure = (reason) => {
    if (typeof window === 'undefined') {
        return false;
    }

    const currentUrl = `${window.location.pathname}${window.location.search}`;
    const lastReloadUrl = window.sessionStorage.getItem(CHUNK_RELOAD_KEY);

    if (lastReloadUrl === currentUrl) {
        console.error('Dynamic import failed after retry; skipping automatic reload.', reason);
        return false;
    }

    window.sessionStorage.setItem(CHUNK_RELOAD_KEY, currentUrl);
    console.warn('Dynamic import failed; reloading page to recover fresh assets.', reason);
    window.location.reload();

    return true;
};

if (typeof window !== 'undefined') {
    window.addEventListener('vite:preloadError', (event) => {
        if (reloadForChunkFailure(event?.payload || event)) {
            event.preventDefault();
        }
    });
}

createInertiaApp({
    id: inertiaRootId,
    page: getInitialInertiaPage(inertiaRootId),
    title: (title) => `${title} - ${appName}`,
    resolve: (name) => {
        // Defensive guard: avoid throwing on undefined component names
        if (!name) {
            const error = new Error('Inertia page name is undefined.');
            console.error(error.message);

            if (reloadForChunkFailure(error)) {
                return new Promise(() => {});
            }

            return Promise.resolve(() => null);
        }

        return resolvePageComponent(`./Pages/${name}.jsx`, pages).catch((error) => {
            if (isDynamicImportFailure(error) && reloadForChunkFailure(error)) {
                return new Promise(() => {});
            }

            throw error;
        });
    },
    setup: async ({ el, App, props }) => {
        const root = createRoot(el);

        // Dynamically import bugsnag if not in local environment
        let ErrorBoundary;
        if (import.meta.env.VITE_APP_ENV !== 'local') {
            const { ErrorBoundary: LoadedErrorBoundary } = await import('@/Utils/bugsnag');
            ErrorBoundary = LoadedErrorBoundary;
        } else {
            // No-op error boundary when local
            ErrorBoundary = ({ children }) => <>{children}</>;
        }

        root.render(
            <ErrorBoundary>
                <NotificationProvider>
                    <App {...props} />
                </NotificationProvider>
            </ErrorBoundary>
        );

        if (typeof window !== 'undefined') {
            window.sessionStorage.removeItem(CHUNK_RELOAD_KEY);
        }
    },
    progress: {
        color: '#4B5563',
    },
});
